import React, { useState, useMemo, useRef } from 'react';
import {
    Box, Grid, TextField, MenuItem, Typography, FormControlLabel, Checkbox
} from '@mui/material';
import {
    Mail as ConvocationIcon
} from '@mui/icons-material';
import GenericPage from '../../Commun/components/GenericPage';
import PrintAgencyHeader from '../components/PrintAgencyHeader';
import { academyStore } from '../services/academyStore';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { useAuth } from '../../Commun/contexts/AuthContext';
import { exportToExcel, exportToWord } from '../utils/printExportUtils';

export const PrintParentConvocationPage: React.FC = () => {
    const { language } = useLanguage();
    const { user } = useAuth();
    const isRtl = language === 'ar';

    const students = academyStore.getStudents();
    const groups = academyStore.getGroups();
    const groupStudents = academyStore.getGroupStudents();

    const todayStr = new Date().toISOString().slice(0, 10);
    const [selectedStudentId, setSelectedStudentId] = useState<number | 'blank'>('blank');
    const [meetingDate, setMeetingDate] = useState<string>(todayStr);
    const [meetingTime, setMeetingTime] = useState<string>('10:00');
    const [selectedReason, setSelectedReason] = useState<string>('results');

    const printableRef = useRef<HTMLDivElement>(null);

    const selectedStudent = useMemo(() => {
        if (selectedStudentId === 'blank') return null;
        return students.find(s => s.id === selectedStudentId) || null;
    }, [students, selectedStudentId]);

    const studentGroup = useMemo(() => {
        if (!selectedStudent) return '';
        const gs = groupStudents.find(g => g.student_id === selectedStudent.id && g.status !== 'Dropped');
        if (gs) {
            const grp = groups.find(g => g.id === gs.group_id);
            if (grp) return grp.name;
        }
        return selectedStudent.school_level || '';
    }, [selectedStudent, groupStudents, groups]);

    const studentFullName = selectedStudent ? `${selectedStudent.last_name} ${selectedStudent.first_name}`.trim() : '—';
    const classLabel = studentGroup || '—';
    const parentName = selectedStudent?.guardian_first_name ? `${selectedStudent.guardian_first_name} ${selectedStudent.guardian_last_name || ''}`.trim() : '—';

    // Export Handlers
    const handleExportExcel = () => {
        const headers = [
            language === 'ar' ? 'الحقل' : language === 'en' ? 'Field' : 'Champ',
            language === 'ar' ? 'القيمة' : language === 'en' ? 'Value' : 'Valeur'
        ];
        const rows = [
            [language === 'ar' ? 'التلميذ' : language === 'en' ? 'Student' : 'Apprenant', studentFullName],
            [language === 'ar' ? 'القسم' : language === 'en' ? 'Class' : 'Classe', classLabel],
            [language === 'ar' ? 'الولي' : language === 'en' ? 'Parent' : 'Parent', parentName],
            [language === 'ar' ? 'تاريخ الموعد' : language === 'en' ? 'Meeting Date' : 'Date rendez-vous', meetingDate],
            [language === 'ar' ? 'التوقيت' : language === 'en' ? 'Time' : 'Heure', meetingTime],
            [language === 'ar' ? 'السبب' : language === 'en' ? 'Reason' : 'Motif', selectedReason]
        ];
        exportToExcel('Convocation_Parent', 'Convocation', headers, rows);
    };

    const handleExportWord = () => {
        if (!printableRef.current) return;
        const title = language === 'ar' ? 'استدعاء ولي التلميذ' : language === 'en' ? 'Parent Convocation' : 'Convocation du parent';
        exportToWord('Convocation_Parent', title, printableRef.current.innerHTML, isRtl);
    };

    const cellStyle = (width?: number, extra?: React.CSSProperties): React.CSSProperties => ({
        border: '1px solid #777',
        padding: '7px 10px',
        textAlign: isRtl ? 'right' : 'left',
        width,
        ...extra
    });

    const reasonsList = [
        { key: 'results', labelFr: 'résultats scolaires', labelAr: 'النتائج المدرسية', labelEn: 'academic results' },
        { key: 'absences', labelFr: 'absences', labelAr: 'الغيابات', labelEn: 'absences' },
        { key: 'behavior', labelFr: 'comportement', labelAr: 'السلوك والانضباط', labelEn: 'behavior' },
        { key: 'file', labelFr: 'dossier incomplet', labelAr: 'ملف غير مكتمل', labelEn: 'incomplete file' },
        { key: 'fees', labelFr: 'frais de scolarité', labelAr: 'المستحقات المدرسية', labelEn: 'tuition fees' },
        { key: 'other', labelFr: 'autre', labelAr: 'سبب آخر', labelEn: 'other' }
    ];

    const filters = (
        <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
                <TextField
                    select
                    fullWidth
                    size="small"
                    label={language === 'ar' ? 'اختيار' : language === 'en' ? 'Select' : 'Choisir'}
                    value={selectedStudentId}
                    onChange={e => setSelectedStudentId(e.target.value === 'blank' ? 'blank' : Number(e.target.value))}
                >
                    <MenuItem value="blank">
                        {language === 'ar' ? '— استدعاء فارغ —' : language === 'en' ? '— Blank Convocation —' : '— Convocation vierge —'}
                    </MenuItem>
                    {students.map(s => (
                        <MenuItem key={s.id} value={s.id}>
                            {s.last_name} {s.first_name} {s.code ? `(${s.code})` : ''}
                        </MenuItem>
                    ))}
                </TextField>
            </Grid>

            <Grid item xs={12} sm={3} md={2.5}>
                <TextField
                    type="date"
                    fullWidth
                    size="small"
                    label={language === 'ar' ? 'تاريخ الموعد' : language === 'en' ? 'Meeting Date' : 'Date du rendez-vous'}
                    value={meetingDate}
                    onChange={e => setMeetingDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                />
            </Grid>

            <Grid item xs={12} sm={3} md={2}>
                <TextField
                    type="time"
                    fullWidth
                    size="small"
                    label={language === 'ar' ? 'الساعة' : language === 'en' ? 'Time' : 'Heure'}
                    value={meetingTime}
                    onChange={e => setMeetingTime(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                />
            </Grid>

            <Grid item xs={12} sm={6} md={3.5}>
                <TextField
                    select
                    fullWidth
                    size="small"
                    label={language === 'ar' ? 'السبب' : language === 'en' ? 'Reason' : 'Motif'}
                    value={selectedReason}
                    onChange={e => setSelectedReason(e.target.value)}
                >
                    {reasonsList.map(r => (
                        <MenuItem key={r.key} value={r.key}>
                            {language === 'ar' ? r.labelAr : language === 'en' ? r.labelEn : r.labelFr}
                        </MenuItem>
                    ))}
                </TextField>
            </Grid>
        </Grid>
    );

    return (
        <GenericPage
            title={language === 'ar' ? 'استدعاء ولي التلميذ' : language === 'en' ? 'Parent Convocation' : 'Convocation du parent'}
            icon={<ConvocationIcon />}
            type="print"
            filters={filters}
            footerEnabled={false}
            onExportExcel={handleExportExcel}
            onExportWord={handleExportWord}
        >
            <div ref={printableRef} dir={isRtl ? 'rtl' : 'ltr'} style={{ maxWidth: 800, margin: '0 auto', color: '#111', fontFamily: 'Arial, sans-serif' }}>
                <PrintAgencyHeader
                    title={language === 'ar' ? 'استدعاء ولي التلميذ' : language === 'en' ? 'PARENT CONVOCATION' : 'CONVOCATION'}
                    showBorderBottom
                />

                <Box sx={{ my: 3 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 25 }}>
                        <tbody>
                            <tr>
                                <td style={cellStyle(190, { fontWeight: 'bold', backgroundColor: '#f8fafc' })}>
                                    {language === 'ar' ? 'التلميذ' : language === 'en' ? 'Student' : 'Apprenant'}
                                </td>
                                <td style={cellStyle(undefined, { fontWeight: 600 })}>{studentFullName}</td>
                            </tr>
                            <tr>
                                <td style={cellStyle(190, { fontWeight: 'bold', backgroundColor: '#f8fafc' })}>
                                    {language === 'ar' ? 'القسم' : language === 'en' ? 'Class' : 'Classe'}
                                </td>
                                <td style={cellStyle()}>{classLabel}</td>
                            </tr>
                            <tr>
                                <td style={cellStyle(190, { fontWeight: 'bold', backgroundColor: '#f8fafc' })}>
                                    {language === 'ar' ? 'الولي' : language === 'en' ? 'Parent / Guardian' : 'Parent'}
                                </td>
                                <td style={cellStyle()}>{parentName}</td>
                            </tr>
                            <tr>
                                <td style={cellStyle(190, { fontWeight: 'bold', backgroundColor: '#f8fafc' })}>
                                    {language === 'ar' ? 'الموعد' : language === 'en' ? 'Appointment' : 'Rendez-vous'}
                                </td>
                                <td style={cellStyle(undefined, { fontWeight: 600 })}>
                                    {meetingDate} &nbsp; {language === 'ar' ? 'على الساعة' : language === 'en' ? 'at' : 'à'} &nbsp; {meetingTime}
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    {/* Invitation Body */}
                    <div style={{ fontSize: 13, lineHeight: 1.8, marginBottom: 20 }}>
                        {language === 'ar' ? (
                            'السيد(ة) ولي التلميذ، يرجى منكم الحضور إلى إدارة المؤسسة في التاريخ والساعة المذكورين أعلاه.'
                        ) : language === 'en' ? (
                            'Dear Parent or Guardian, you are kindly requested to attend a meeting at the school administration on the date and time indicated above.'
                        ) : (
                            "Madame, Monsieur, vous êtes prié(e) de bien vouloir vous présenter à l'établissement à la date et à l'heure indiquées ci-dessus."
                        )}
                    </div>

                    {/* Motives Checkboxes */}
                    <div style={{ fontSize: 12, marginBottom: 25, padding: 10, backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 4 }}>
                        <strong>{language === 'ar' ? 'السبب :' : language === 'en' ? 'Reason :' : 'Motif :'}</strong> &nbsp;&nbsp;
                        {reasonsList.map(r => {
                            const isChecked = selectedReason === r.key;
                            const label = language === 'ar' ? r.labelAr : language === 'en' ? r.labelEn : r.labelFr;
                            return (
                                <span key={r.key} style={{ marginInlineEnd: 15, display: 'inline-block' }}>
                                    [{isChecked ? '✓' : ' '}] {label}
                                </span>
                            );
                        })}
                    </div>

                    <div style={{ textAlign: isRtl ? 'left' : 'right', marginBottom: 25, fontSize: 12 }}>
                        <strong>{language === 'ar' ? 'إدارة المؤسسة' : language === 'en' ? 'School Administration' : "L'administration"}</strong>
                    </div>

                    {/* Perforated Divider */}
                    <div style={{ borderTop: '1px dashed #777', margin: '30px 0 20px 0', position: 'relative', textAlign: 'center' }}>
                        <span style={{ position: 'relative', top: -10, backgroundColor: '#fff', padding: '0 10px', fontSize: 11, color: '#888' }}>
                            ✂ {language === 'ar' ? 'الجزء الواجب إرجاعه موقعاً' : language === 'en' ? 'Tear-off slip to be returned signed' : 'Talon à retourner signé'}
                        </span>
                    </div>

                    {/* Return Slip */}
                    <div style={{ fontSize: 12, lineHeight: 1.8, marginBottom: 20 }}>
                        {language === 'ar' ? (
                            <>
                                أنا الممضي أسفله .................................... ولي التلميذ(ة) .................................... أقر باستلام استدعاء الحضور ليوم <strong>{meetingDate}</strong> على الساعة <strong>{meetingTime}</strong>.
                                <br />
                                [ ] سأكون حاضراً في الموعد المحدد &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; [ ] أطلب تأجيل الموعد إلى موعد آخر
                            </>
                        ) : language === 'en' ? (
                            <>
                                I, the undersigned .................................... parent/guardian of student .................................... acknowledge receipt of the summons for <strong>{meetingDate}</strong> at <strong>{meetingTime}</strong>.
                                <br />
                                [ ] I will attend at the scheduled time &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; [ ] I request an alternative appointment
                            </>
                        ) : (
                            <>
                                Je soussigné(e) .................................... parent de l'apprenant .................................... déclare avoir pris connaissance de la convocation du <strong>{meetingDate}</strong> à <strong>{meetingTime}</strong>.
                                <br />
                                [ ] Je serai présent(e) &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; [ ] Je demande un autre rendez-vous
                            </>
                        )}
                    </div>

                    {/* Signatures */}
                    <table style={{ width: '100%', marginTop: 25, borderCollapse: 'collapse', fontSize: 12 }}>
                        <tbody>
                            <tr>
                                <td style={{ width: '50%', border: 'none', textAlign: isRtl ? 'right' : 'left' }}>
                                    <strong>{language === 'ar' ? 'توقيع الولي :' : language === 'en' ? 'Parent Signature :' : 'Le parent (signature) :'}</strong>
                                    <div style={{ height: 45 }} />
                                    <div style={{ borderTop: '1px solid #999', width: '60%' }} />
                                </td>
                                <td style={{ width: '50%', border: 'none', textAlign: isRtl ? 'left' : 'right' }}>
                                    <strong>{language === 'ar' ? 'المدير (الختم) :' : language === 'en' ? 'Director (Stamp) :' : 'Le directeur (cachet) :'}</strong>
                                    <div style={{ height: 45 }} />
                                    <div style={{ borderTop: '1px solid #999', width: '60%', marginInlineStart: 'auto' }} />
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <div style={{ marginTop: 25, textAlign: 'center', fontSize: 11, color: '#666' }}>
                        {language === 'ar' ? 'شكراً على ثقتكم.' : language === 'en' ? 'Thank you for your trust.' : 'Merci de votre confiance.'}
                    </div>
                </Box>
            </div>
        </GenericPage>
    );
};

export default PrintParentConvocationPage;
